import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ShareService, CreateShareRequest } from '../services/share.service';
import { useAuth } from 'oidc-react';

// Helper to extract error message from various error formats
const getErrorMessage = (error: any, fallback: string): string => {
  // Handle nested validation errors: { message: { message: ["error1", "error2"] } }
  if (error.response?.data?.message?.message && Array.isArray(error.response.data.message.message)) {
    return error.response.data.message.message.join(', ');
  }
  // Handle simple message: { message: "error" }
  if (typeof error.response?.data?.message === 'string') {
    return error.response.data.message;
  }
  // Handle error.message
  if (typeof error.message === 'string') {
    return error.message;
  }
  return fallback;
};

export const useShares = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { userData } = useAuth();
  const isAuthenticated = !!userData;

  const {
    data: shares = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['shares'],
    queryFn: () => ShareService.getShares(),
    staleTime: 5 * 60 * 1000, // 5 minutes - shorter cache for shares
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in React Query v5)
    refetchOnWindowFocus: false,
    refetchOnMount: 'always', // Always refetch on mount to prevent stale data on dashboard (AG-117)
    enabled: isAuthenticated, // Only run query if user is authenticated
  });

  const createShareMutation = useMutation({
    mutationFn: (shareData: CreateShareRequest) => ShareService.createShare(shareData),
    onSuccess: (newShare) => {
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      queryClient.invalidateQueries({ queryKey: ['control-shares', newShare.control.id] });
      queryClient.invalidateQueries({ queryKey: ['recentActivities'] });
      // Toast removed - share is created automatically when modal opens, not a user action
    },
    onError: (error: any) => {
      console.error('Create share error:', error);
      enqueueSnackbar(
        getErrorMessage(error, 'Misslyckades att skapa delning'),
        { variant: 'error' }
      );
    },
  });

  const updateShareStatusMutation = useMutation({
    mutationFn: ({ shareId, isActive }: { shareId: string; isActive: boolean }) =>
      ShareService.updateShareStatus(shareId, isActive),
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      enqueueSnackbar(
        `Delning ${isActive ? 'aktiverad' : 'inaktiverad'}`,
        { variant: 'success' }
      );
    },
    onError: (error: any) => {
      console.error('Update share status error:', error);
      enqueueSnackbar(
        getErrorMessage(error, 'Misslyckades att uppdatera delning'),
        { variant: 'error' }
      );
    },
  });

  const updateShareExpirationMutation = useMutation({
    mutationFn: ({ shareId, expiresInHours }: { shareId: string; expiresInHours: number; silent?: boolean }) =>
      ShareService.updateShareExpiration(shareId, expiresInHours),
    onSuccess: (updatedShare, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      queryClient.invalidateQueries({ queryKey: ['control-shares', updatedShare.control.id] });
      if (!variables.silent) {
        enqueueSnackbar('Utgångstid uppdaterad', { variant: 'success' });
      }
    },
    onError: (error: any) => {
      console.error('Update share expiration error:', error);
      enqueueSnackbar(
        getErrorMessage(error, 'Misslyckades att uppdatera utgångstid'),
        { variant: 'error' }
      );
    },
  });

  const deleteShareMutation = useMutation({
    mutationFn: (shareId: string) => ShareService.deleteShare(shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      enqueueSnackbar('Delning borttagen', {
        variant: 'success'
      });
    },
    onError: (error: any) => {
      console.error('Delete share error:', error);
      enqueueSnackbar(
        getErrorMessage(error, 'Misslyckades att ta bort delning'),
        { variant: 'error' }
      );
    },
  });

  const copyShareUrl = async (shareUrl: string) => {
    const success = await ShareService.copyShareUrl(shareUrl);
    if (success) {
      enqueueSnackbar('Länk kopierad till urklipp', {
        variant: 'success',
        autoHideDuration: 2000
      });
    } else {
      enqueueSnackbar('Misslyckades att kopiera länk', {
        variant: 'error'
      });
    }
    return success;
  };

  return {
    // Data
    shares,
    isLoading,
    error,

    // Actions
    refetch,
    createShare: createShareMutation.mutateAsync,
    updateShareStatus: updateShareStatusMutation.mutateAsync,
    updateShareExpiration: updateShareExpirationMutation.mutateAsync,
    deleteShare: deleteShareMutation.mutateAsync,
    copyShareUrl,

    // Loading states
    isCreating: createShareMutation.isPending,
    isUpdating: updateShareStatusMutation.isPending || updateShareExpirationMutation.isPending,
    isDeleting: deleteShareMutation.isPending,

    // Utility functions
    formatAccessLevel: ShareService.formatAccessLevel,
    isExpired: ShareService.isExpired,
    formatTimeUntilExpiration: ShareService.formatTimeUntilExpiration,
    getAccessLevelOptions: ShareService.getAccessLevelOptions,
    getExpirationOptions: ShareService.getExpirationOptions,
  };
};

export const useControlShares = (controlId: string, includeExpired = false) => {
  const { userData, isLoading: oidcLoading } = useAuth();
  const accessToken = userData?.access_token;
  const isAuthenticated = !!accessToken;

  const {
    data: shares = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['control-shares', controlId, includeExpired],
    queryFn: () => ShareService.getControlShares(controlId, includeExpired),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: isAuthenticated && !oidcLoading && !!controlId,
    refetchOnWindowFocus: true, // Refetch when window regains focus to catch updates
  });

  // Get the first active share (most recent)
  const activeShare = shares.find(share => share.isActive && !ShareService.isExpired(share.expiresAt));

  return {
    // Data
    shares,
    activeShare,
    hasActiveShare: !!activeShare,
    isLoading,
    error,

    // Actions
    refetch,

    // Utility functions
    formatAccessLevel: ShareService.formatAccessLevel,
    isExpired: ShareService.isExpired,
    formatTimeUntilExpiration: ShareService.formatTimeUntilExpiration,
  };
};

export const usePublicShare = (token: string, enabled: boolean = true, sessionToken?: string) => {
  // const { enqueueSnackbar } = useSnackbar();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['public-share', token, sessionToken],
    queryFn: () => ShareService.accessPublicShare(token, sessionToken),
    staleTime: 60 * 1000, // 1 minute - short cache for public access
    gcTime: 2 * 60 * 1000, // 2 minutes (renamed from cacheTime in React Query v5)
    enabled: !!token && enabled, // Only fetch if token exists AND enabled flag is true
    retry: false, // Don't retry on failed public access
  });

  const {
    data: shareInfo,
    isLoading: isLoadingInfo,
  } = useQuery({
    queryKey: ['public-share-info', token],
    queryFn: () => ShareService.getPublicShareInfo(token),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes (renamed from cacheTime in React Query v5)
    enabled: !!token && enabled, // Only fetch if token exists AND enabled flag is true
    retry: false,
  });

  return {
    // Data
    shareData: data,
    shareInfo,
    isLoading: isLoading || isLoadingInfo,
    error,

    // Actions
    refetch,

    // Utility functions
    formatAccessLevel: ShareService.formatAccessLevel,
    isExpired: ShareService.isExpired,
    formatTimeUntilExpiration: ShareService.formatTimeUntilExpiration,
  };
};